import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import validator from "validator";

import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";

import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import i18n from "../i18n";

const normalizeLanguage = language => {
  return language && language.toLowerCase().startsWith("fr") ? "fr" : "en";
};

const Settings = () => {
  const { currentUser, updateEmail, updatePassword, updateUserData } =
    useAuth();

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    password: "",
    newPassword: "",
    company: "",
    address: "",
    siret: "",
    url: "",
    phone: "",
    bic: "",
    iban: "",
    invoice: "",
    quote: "",
    logo: "",
    language: normalizeLanguage(i18n.resolvedLanguage)
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        const userDoc = doc(db, `users/${currentUser.uid}`);
        const docSnap = await getDoc(userDoc);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(prev => ({
            ...prev,
            ...data,
            language: normalizeLanguage(data.language || i18n.resolvedLanguage)
          }));
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
    // Reset errors on input change
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateIBAN = () => {
    if (!validator.isIBAN(userData.iban)) {
      setErrors(prev => ({ ...prev, iban: "Invalid IBAN format" }));
      return false;
    }
    return true;
  };

  const validateBIC = () => {
    if (!validator.isBIC(userData.bic)) {
      setErrors(prev => ({ ...prev, bic: "Invalid BIC format" }));
      return false;
    }
    return true;
  };

  const handleSaveIban = async () => {
    if (!validateIBAN()) {
      enqueueSnackbar("Il faut entrer un IBAN valide.", {
        variant: "error"
      });
      return;
    }

    if (!validateBIC()) {
      enqueueSnackbar("Il faut entrer un BIC valide.", {
        variant: "error"
      });
      return;
    }

    try {
      const userDoc = doc(db, `users/${currentUser.uid}`);
      await updateDoc(userDoc, { iban: userData.iban, bic: userData.bic });
      enqueueSnackbar("IBAN/BIC mis à jour avec succès", {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(`Error updating IBAN: ${error.message}`, {
        variant: "error"
      });
    }
  };

  const handleSaveInvoiceNumber = async () => {
    try {
      const userDoc = doc(db, `users/${currentUser.uid}`);
      await updateDoc(userDoc, {
        invoice: userData.invoice,
        quote: userData.quote
      });
      enqueueSnackbar("Numéros de facture et devis mis à jour avec succès", {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(
        `Error updating invoice/quote numbers: ${error.message}`,
        {
          variant: "error"
        }
      );
    }
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (
      file &&
      file.size <= 350000 &&
      ["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(file.type)
    ) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const newLogo = reader.result;
        setUserData({ ...userData, logo: newLogo });
        await updateUserData({ logo: newLogo });
        enqueueSnackbar("Logo mis à jour avec succès", { variant: "success" });
      };
      reader.readAsDataURL(file);
    } else {
      enqueueSnackbar("Invalid file format or size", { variant: "error" });
    }
  };

  const handleSave = async e => {
    e.preventDefault();

    try {
      const userDoc = doc(db, `users/${currentUser.uid}`);
      await updateDoc(userDoc, userData);
      enqueueSnackbar("Paramètre mis à jour avec succès", {
        variant: "success"
      });

      if (userData.email !== currentUser.email) {
        await updateEmail(userData.email);
      }
      if (userData.newPassword) {
        await updatePassword(userData.newPassword);
      }
    } catch (error) {
      enqueueSnackbar(`Error updating settings: ${error.message}`, {
        variant: "error"
      });
    }
  };

  const handleFieldSave = async field => {
    try {
      const userDoc = doc(db, `users/${currentUser.uid}`);
      await updateDoc(userDoc, { [field]: userData[field] });
      enqueueSnackbar("Paramètre mis à jour avec succès", {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(`Error updating ${field}: ${error.message}`, {
        variant: "error"
      });
    }
  };

  const handleLanguageChange = async event => {
    const language = normalizeLanguage(event.target.value);
    setUserData(prev => ({ ...prev, language }));

    try {
      await updateUserData({ language });
      enqueueSnackbar(t("settings.languageUpdated"), {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(`Error updating language: ${error.message}`, {
        variant: "error"
      });
    }
  };

  return (
    <Box sx={{ maxWidth: "100%", mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: theme => `1px solid ${theme.palette.divider}`,
          background: theme =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(71, 85, 105, 0.16))"
              : "linear-gradient(135deg, #f8fafc, #eef2f7)"
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "primary.main",
                fontWeight: 700
              }}
            >
              Réglages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Personnalisation du compte et de l&apos;entreprise
            </Typography>
          </Box>
          <Chip
            label={normalizeLanguage(userData.language) === "fr" ? "Français" : "English"}
            variant="outlined"
            sx={{
              backgroundColor: theme =>
                theme.palette.mode === "dark"
                  ? "rgba(148, 163, 184, 0.12)"
                  : "rgba(15, 23, 42, 0.04)",
              borderColor: theme => theme.palette.divider
            }}
          />
        </Stack>
      </Paper>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Changer vos informations personnelles
            </Typography>
            <Box
              component="form"
              onSubmit={handleSave}
              noValidate
              sx={{ mt: 1 }}
            >
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="name"
                label="Nom"
                name="name"
                autoComplete="name"
                value={userData.name}
                onChange={handleInputChange}
                onBlur={() => handleFieldSave("name")}
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                value={userData.email}
                onChange={handleInputChange}
                onBlur={() => handleFieldSave("email")}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                name="password"
                label="Ancien mot de passe"
                type="password"
                id="password"
                autoComplete="current-password"
                value={userData.password}
                onChange={handleInputChange}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                name="newPassword"
                label="Nouveau mot de passe"
                type="password"
                id="newPassword"
                value={userData.newPassword}
                onChange={handleInputChange}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2 }}
              >
                Sauvegarder
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Changer les informations de votre entreprise
            </Typography>
            <Box
              component="form"
              onSubmit={handleSave}
              noValidate
              sx={{ mt: 1 }}
            >
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="company"
                label="Nom de l'entreprise"
                name="company"
                value={userData.company}
                onChange={handleInputChange}
                onBlur={() => handleFieldSave("company")}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="address"
                label="Adresse"
                name="address"
                value={userData.address}
                onChange={handleInputChange}
                onBlur={() => handleFieldSave("address")}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="siret"
                label="Siret"
                name="siret"
                value={userData.siret}
                onChange={handleInputChange}
                onBlur={() => handleFieldSave("siret")}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="url"
                label="Site Web"
                name="url"
                value={userData.url}
                onChange={handleInputChange}
                onBlur={() => handleFieldSave("url")}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="phone"
                label="Téléphone"
                name="phone"
                value={userData.phone}
                onChange={handleInputChange}
                onBlur={() => handleFieldSave("phone")}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pour personnaliser vos devis et factures
            </Typography>
            <Box
              component="form"
              onSubmit={handleSave}
              noValidate
              sx={{ mt: 1 }}
            >
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="bic"
                label="Code BIC"
                name="bic"
                value={userData.bic}
                error={!!errors.bic}
                helperText={errors.bic}
                onChange={handleInputChange}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="iban"
                label="IBAN"
                name="iban"
                value={userData.iban}
                error={!!errors.iban}
                helperText={errors.iban}
                onChange={handleInputChange}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2 }}
                onClick={handleSaveIban}
              >
                Sauvegarder
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Personnaliser votre interface, vos factures et devis
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Pour des raisons légales, vous ne pouvez plus éditer vos numéros
              de facture et devis une fois définies.
            </Typography>
            <Box component="form" noValidate sx={{ mt: 1 }}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="invoice"
                label="Numéro de Facture"
                name="invoice"
                value={userData.invoice}
                error={!!errors.invoice}
                helperText={errors.invoice}
                onChange={handleInputChange}
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="quote"
                label="Numéro de Devis"
                name="quote"
                value={userData.quote}
                error={!!errors.quote}
                helperText={errors.quote}
                onChange={handleInputChange}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2 }}
                onClick={handleSaveInvoiceNumber}
              >
                Sauvegarder
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Personnaliser votre interface
            </Typography>
            <TextField
              select
              variant="outlined"
              margin="normal"
              fullWidth
              id="language"
              label={t("settings.language")}
              name="language"
              value={normalizeLanguage(userData.language)}
              onChange={handleLanguageChange}
            >
              <MenuItem value="fr">{t("settings.languages.fr")}</MenuItem>
              <MenuItem value="en">{t("settings.languages.en")}</MenuItem>
            </TextField>
            <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
              <Avatar
                src={userData.logo}
                alt="Logo"
                sx={{ width: 80, height: 80, marginRight: 2 }}
              />
              <input
                accept="image/jpeg, image/png, image/gif"
                style={{ display: "none" }}
                id="raised-button-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="raised-button-file">
                <Button variant="contained" color="primary" component="span">
                  Changer de logo
                </Button>
              </label>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
export default Settings;
